import React, { useState, useEffect } from 'react';
import { db, auth, hasConfig } from './firebaseconfig';
import { doc, getDoc } from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate
} from 'react-router-dom';

import SidewalkRampPage from './pages/SidewalkRampPage';
import CaseDetailPage from './pages/CaseDetailPage';
import AdminCasesPage from './pages/AdminCasesPage';
import DashboardPage from './DashboardPage';
import AdminDashboard from './AdminDashboard';
import Map from './Map';
import PermitForm from './components/PermitForm';
import AdminPermitTable from './AdminPermitTable';
import DashboardLayout from './DashboardLayout';
import Login from './components/Login';
import SignupPage from './SignupPage';
import PermitHistory from './components/PermitHistory';
import PermitList from './components/PermitList';
import PermitDetails from './components/PermitDetails';
import ContactSupportPage from './components/ContactSupportPage';
import UserSettingsPage from './components/UserSettingsPage';
import SubmitPermitForm from './SubmitPermitForm';
import PilotDemoPage from './pages/PilotDemoPage';
import DemoDeckRedirect from './pages/DemoDeckRedirect';
import VendorPortalPage from './pages/VendorPortalPage';

import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import './App.css';
import './robinhoodspace.css';

const LogoutPage = ({ onLogout }) => {
  useEffect(() => {
    onLogout();
  }, [onLogout]);

  return <h2>Logging out...</h2>;
};

const AdminRoute = ({ isAdmin, children }) => {
  return isAdmin ? children : <Navigate to="/dashboard" />;
};

const ProtectedLayout = ({ user, isAdmin, children }) => (
  <DashboardLayout email={user.email} isAdmin={isAdmin}>
    {children}
  </DashboardLayout>
);

const AppContent = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!hasConfig || !auth) {
      setCheckingAuth(false);
      setError('Firebase is not configured for this build. Contact support.');
      return undefined;
    }

    const authRef = auth;
    const unsubscribe = onAuthStateChanged(authRef, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (db) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsAdmin(userData.role === 'admin');
          }
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setCheckingAuth(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    if (!auth) {
      setError('Firebase is not configured for this build.');
      return;
    }
    setLoading(true);
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(email)) {
      setError("Invalid email format");
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(userCredential.user);

      if (!db) {
        setError('User profile store is not available.');
        setLoading(false);
        return;
      }
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setIsAdmin(userData.role === 'admin');
        navigate(userData.role === 'admin' ? '/admin' : '/dashboard');
      } else {
        setError("User profile not found.");
      }
    } catch (error) {
      setError("Error logging in: " + error.message);
      console.error(error.message);
    }

    setLoading(false);
  };

  const handleLogout = () => {
    if (auth) signOut(auth);
    setUser(null);
    setIsAdmin(false);
    navigate('/');
  };

  if (!hasConfig || !auth) {
    return (
      <div className="san-diego-alarm-program">
        <div className="sd-login-box">
          <p className="rhs-login-brand">RobinhoodSpace</p>
          <p className="rhs-error" role="alert">
            {error || 'Firebase is not configured for this build. Contact support.'}
          </p>
        </div>
      </div>
    );
  }

  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-md">
          <svg
            className="animate-spin h-8 w-8 text-blue-500 mb-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={isAdmin ? "/admin" : "/dashboard"} />
          ) : (
            <Login
              onLogin={handleLogin}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              error={error}
              loading={loading}
            />
          )
        }
      />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/demo" element={<PilotDemoPage user={user} isAdmin={isAdmin} />} />
      <Route path="/demo/deck" element={<DemoDeckRedirect />} />

      {user && (
        <>
          <Route
            path="/dashboard"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <DashboardPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute isAdmin={isAdmin}>
                <ProtectedLayout user={user} isAdmin={isAdmin}>
                  <AdminDashboard />
                </ProtectedLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/table"
            element={
              <AdminRoute isAdmin={isAdmin}>
                <ProtectedLayout user={user} isAdmin={isAdmin}>
                  <AdminPermitTable />
                </ProtectedLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cases"
            element={
              <AdminRoute isAdmin={isAdmin}>
                <ProtectedLayout user={user} isAdmin={isAdmin}>
                  <AdminCasesPage />
                </ProtectedLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/vendor-portal"
            element={
              <AdminRoute isAdmin={isAdmin}>
                <ProtectedLayout user={user} isAdmin={isAdmin}>
                  <VendorPortalPage />
                </ProtectedLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cases/:caseId"
            element={
              <AdminRoute isAdmin={isAdmin}>
                <ProtectedLayout user={user} isAdmin={isAdmin}>
                  <CaseDetailPage />
                </ProtectedLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/cases/:caseId"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <CaseDetailPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/map"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <Map />
              </ProtectedLayout>
            }
          />
          <Route
            path="/permit"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <PermitForm />
              </ProtectedLayout>
            }
          />
          <Route
            path="/permit/submit"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <SubmitPermitForm />
              </ProtectedLayout>
            }
          />
          <Route
            path="/permit-list"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <PermitList />
              </ProtectedLayout>
            }
          />
          <Route
            path="/permit-details/:permitId"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <PermitDetails />
              </ProtectedLayout>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <UserSettingsPage user={user} />
              </ProtectedLayout>
            }
          />
          <Route
            path="/permit-history"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <PermitHistory />
              </ProtectedLayout>
            }
          />
          <Route
            path="/contact-support"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <ContactSupportPage />
              </ProtectedLayout>
            }
          />
          <Route
            path="/sidewalk-ramp"
            element={
              <ProtectedLayout user={user} isAdmin={isAdmin}>
                <SidewalkRampPage />
              </ProtectedLayout>
            }
          />
          <Route path="/logout" element={<LogoutPage onLogout={handleLogout} />} />
        </>
      )}

      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} />} />
    </Routes>
  );
};

const App = () => (
  <GoogleMapsProvider>
    <Router>
      <AppContent />
    </Router>
  </GoogleMapsProvider>
);

export default App;
